import Link from "next/link";
import { Container } from "@/components/molecules/Container";
import { Inner } from "@/components/molecules/Inner";
import { Tag } from "@/components/molecules/Tag";
import { BreadCrumb } from "@/components/organisms/BreadCrumb";
import Careers from "@/components/organisms/Careers";
import Contact from "@/components/organisms/Contact";
import { Footer } from "@/components/templates/Footer";
import { Header } from "@/components/templates/Header";
import Layout from "@/components/templates/Layout";
import Meta from "@/components/templates/Meta";
import { useBudouX } from "@/hooks/useBudouX";
import { AI_CASES, getAiCase } from "@/data/aiCases";

export async function getStaticPaths() {
  return {
    paths: AI_CASES.map((c) => ({ params: { slug: c.slug } })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const data = getAiCase(params.slug) || null;
  return { props: { data } };
}

// <br /> を除いたプレーンなタイトル（Meta・パンくず用）
const plainTitle = (t) => (t || "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();

export default function AiCaseArticle({ data }) {
  const { parse } = useBudouX();
  if (!data) return null;
  const title = plainTitle(data.title);
  // 詳細カバー用は絵柄を大きくした画像（/ai-case/lg/）を使用
  const coverImg = data.img.replace("/ai-case/", "/ai-case/lg/");

  return (
    <>
      <Meta
        pageTitle={`${title} | AI活用事例`}
        pageDescription={`株式会社トッカシステムズのAI活用事例。${data.intro}`}
        pagePath={`https://tocca.systems/project/ai/${data.slug}`}
        imgUrl={`/public${data.img}`}
      />
      <Layout>
        <Header isRelative={true} />
        <main className="flex-1">
          <Container>
            <BreadCrumb
              lists={[
                { name: "TOP", path: "/" },
                { name: "Project", path: "/project/" },
                { name: title },
              ]}
            />
            <Inner className="pt-0 pb-10 md:pt-6 md:pb-20">
              {/* cover */}
              <div className="px-0 md:px-4 lg:px-0">
                <div
                  className={`relative box-border flex aspect-1.4/1 items-end justify-start overflow-hidden px-4 pt-16 pb-4 text-txt-base sm:aspect-2/1 md:px-8 md:py-6 lg:px-12 lg:py-10 ${data.pastel}`}
                >
                  <div className="relative z-10 max-w-[58%] md:max-w-[52%]">
                    <h6 className="mb-4 text-sm font-bold text-pri md:mb-8 md:text-md">
                      {data.category}
                    </h6>
                    <h2 className="mb-4 font-bold leading-relaxed fluid-text-[18px,36px]">
                      <span dangerouslySetInnerHTML={{ __html: data.title }} />
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {data.tags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </div>
                  </div>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={coverImg}
                    alt={title}
                    className="absolute right-0 bottom-2 w-[46%] max-w-[380px] md:right-2 md:bottom-1/2 md:w-[50%] md:max-w-[520px] md:translate-y-1/2"
                  />
                </div>
                <div className="border border-x-0 border-t-0 border-solid border-bdc-light p-4 leading-comfort text-txt-base fluid-text-[14px,17px] md:border-x md:border-bdc-base md:px-8 md:py-6 lg:px-14 lg:py-8">
                  {parse(data.intro)}
                </div>
              </div>

              {/* body */}
              <div className="mx-auto max-w-2xl space-y-8 py-8 md:py-10 lg:pt-14 lg:pb-16">
                {data.sections.map((s) => (
                  <div key={s.h} className="space-y-2 md:space-y-3">
                    <h3 className="font-bold text-pri fluid-text-[16px,20px]">
                      {parse(s.h)}
                    </h3>
                    <p className="leading-loose text-txt-base fluid-text-[14px,16px]">
                      {parse(s.b)}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 border border-x-0 border-b-0 border-solid border-bdc-light pt-8 sm:flex-row sm:justify-between">
                <Link href="/project/">
                  <a className="text-sm font-bold text-txt-light transition-colors hover:text-pri">
                    ← AI活用事例の一覧へ
                  </a>
                </Link>
                <Link href="/contact/form/">
                  <a className="inline-flex items-center gap-2 rounded-full bg-pri px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-hov">
                    この事例について相談する <span>→</span>
                  </a>
                </Link>
              </div>
            </Inner>
          </Container>
        </main>
        <Careers />
        <Contact />
        <Footer />
      </Layout>
    </>
  );
}
